import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { chain, isEqual } from 'lodash';
import type { Subject } from 'rxjs';
import { distinctUntilChanged, forkJoin } from 'rxjs';
import { DriversTachographComponent } from '../../../../../../common/components/drivers-tachograph/drivers-tachograph.component';
import type {
  Driver,
  SimplifiedDriver,
} from '../../../../../drivers/common/interfaces/driver';
import type {
  Fleet,
  SimplifiedFleet,
} from '../../../../../fleet/common/interfaces/fleet';
import { OrderPlanningMode } from '../../../../common/enums/order-planning-mode';
import { OrderStatus } from '../../../../common/enums/order-status';
import { OrderHelper } from '../../../../common/helpers/order.helper';
import type {
  AssignationData,
  Order,
} from '../../../../common/interfaces/order';
import { OrderModalOutsourcingDataComponent } from '../order-modal-outsourcing-data/order-modal-outsourcing-data.component';
import { OrderModalDecisionButtonsComponent } from '../order-modal-summary-main-section/order-modal-decision-buttons/order-modal-decision-buttons.component';
import { OrderModalVehiclesComponent } from '../order-modal-summary-main-section/order-modal-vehicles/order-modal-vehicles.component';
import { DriversService } from './../../../../../drivers/common/services/drivers.service';

interface OrderModalAssignmentDrivers {
  primaryDriver: SimplifiedDriver | null;
  secondaryDriver: SimplifiedDriver | null;
}

@Component({
  selector: 'app-order-modal-assignment',
  imports: [
    OrderModalVehiclesComponent,
    DriversTachographComponent,
    OrderModalDecisionButtonsComponent,
    OrderModalOutsourcingDataComponent,
  ],
  templateUrl: './order-modal-assignment.component.html',
  styleUrl: './order-modal-assignment.component.scss',
})
export class OrderModalAssignmentComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly driversService = inject(DriversService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly order = input.required<Order>();
  public readonly selectedManualVehicle = input<Fleet | null>(null);
  public readonly tab = model<OrderPlanningMode>(OrderPlanningMode.AUTO);
  public readonly close$ = input.required<Subject<void>>();

  protected readonly OrderHelper = OrderHelper;
  protected readonly OrderPlanningMode = OrderPlanningMode;
  protected readonly OrderStatus = OrderStatus;

  protected vehicle = signal<Fleet | SimplifiedFleet | null>(null);
  protected trailer = signal<SimplifiedFleet | null>(null);
  protected drivers = signal<(Driver | SimplifiedDriver)[] | null>(null);
  protected driversLoading = signal(true);
  protected manualProposal = linkedSignal(() =>
    OrderHelper.getManualProposal(this.order()),
  );

  protected status = computed(() => this.order().status.value);
  protected hasEngineProposal = computed(
    () => !!OrderHelper.getEngineProposal(this.order()),
  );
  protected currentPointServiceTime = computed(
    () => OrderHelper.getCurrentPoint(this.order().points)?.serviceTime,
  );
  protected hasApprovedVehicle = computed(() =>
    OrderHelper.hasApprovedVehicle(this.order()),
  );
  protected missingAssignment = computed(() =>
    OrderHelper.getMissingAssignment(this.order()),
  );

  public ngOnInit(): void {
    this.tab.update((tab) => {
      if (this.hasEngineProposal() && !this.manualProposal()) {
        return OrderPlanningMode.AUTO;
      }
      if (this.manualProposal()) return OrderPlanningMode.MANUAL;
      return tab;
    });

    effect(
      () => {
        const order = this.order();
        const selectedManualVehicle = this.selectedManualVehicle();

        let data:
          | (Pick<AssignationData, 'vehicle' | 'trailer'> &
              OrderModalAssignmentDrivers)
          | null = null;
        if (selectedManualVehicle && this.tab() === OrderPlanningMode.MANUAL) {
          data = {
            vehicle: selectedManualVehicle,
            trailer: selectedManualVehicle.trailer ?? null,
            primaryDriver:
              selectedManualVehicle?.drivers?.primaryDriver ?? null,
            secondaryDriver:
              selectedManualVehicle?.drivers?.secondaryDriver ?? null,
          };
        } else if (order.assignedData) {
          data = order.assignedData;
        } else if (this.hasEngineProposal()) {
          data = order.proposalData;
        }

        const { primaryDriver, secondaryDriver } = data ?? {};
        const drivers =
          chain([primaryDriver, secondaryDriver]).compact().value() ?? null;

        this.vehicle.set(data?.vehicle ?? null);
        this.trailer.set(data?.trailer ?? null);
        this.drivers.set(drivers);

        const drivers$ = chain(drivers)
          .map(({ uuid }) => this.driversService.getDriver(uuid))
          .value();

        this.driversLoading.set(true);
        forkJoin(drivers$)
          .pipe(
            distinctUntilChanged((previous, current) =>
              isEqual(previous, current),
            ),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe((drivers) => this.drivers.set(drivers))
          .add(() => this.driversLoading.set(false));
      },
      { injector: this.injector },
    );
  }
}
